import dayjs from "dayjs";
import { DateRange, RequoteReason, RequoteReasonEnum } from "redmond";
import { PRODUCT_NAME } from "../textConstants";

export const UPDATE_TRAVELERS_CTA_TEXT = "Update travelers";
export const SELECT_ANOTHER_HOTEL_CTA_TEXT = "Select another hotel";
export const SELECT_ANOTHER_HOTEL_ROOM_CTA_TEXT = "Select another hotel room";
export const CONTINUE_CTA_TEXT = "Continue";
export const SELECT_ANOTHER_PACKAGE_CTA_TEXT = () => {
  const productName = PRODUCT_NAME();
  return `Select another ${productName}`;
};
export const SELECT_ANOTHER_FLIGHT_CTA_TEXT = "Select another flight";
export const TRY_AGAIN_CTA_TEXT = "Try again";

export const GENERIC_ERROR_TITLE = "There was an error.";
export const INVALID_PASSENGERS_TITLE = "Invalid Passengers";
export const PASSENGER_VALIDATION_TITLE = "Passenger Validation Error";
export const UNABLE_TO_COMPLETE_BOOKING_TITLE =
  "Unable to complete your booking";
export const NO_AVAILABILITY_TITLE = "No Availability";
export const MISSING_CONTACT_INFO_TITLE = "Missing Contact Information";
export const TOO_MANY_PASSENGERS_TITLE = "Too Many Passengers";
export const UPGRADE_REQUIRED_TITLE = "Upgrade is required";
export const BOOKING_FAILED_TITLE = "We were unable to process your booking.";

export const GENERIC_ERROR_SUBTITLE = () => {
  const productName = PRODUCT_NAME();
  return `There was an error, reload the page or select another ${productName}.`;
};
export const GENERIC_ERROR_TRY_AGAIN_SUBTITLE =
  "There was an error, please try again or reload the page.";

// flights
export const MIN_AGE_NOT_MET_SUBTITLE =
  "Minors must be accompanied by at least one adult. Ensure all information matches your Travel ID and is up to date.";
export const DUPLICATED_PASSPORT_SUBTITLE =
  "It looks like you entered the same passport more than once. Ensure all information matches your Travel ID and is up to date.";
export const EXPIRING_PASSPORT_SUBTITLE =
  "Your passport appears to be expiring soon. Ensure all information matches your Travel ID and is up to date.";
export const EXPIRED_PASSPORT_SUBTITLE =
  "Your passport appears to be expired. Ensure all information matches your Travel ID and is up to date.";
export const INFANT_KTN_SUBTITLE =
  "It looks like you've added a known traveler number for a lap infant, but lap infants cannot have known traveler numbers.";
export const INFANT_REDRESS_SUBTITLE =
  "It looks like you've added a redress number for a lap infant, but lap infants cannot have redress numbers.";
export const INVALID_PASSENGERS_SUBTITLE =
  "It looks like your information is incorrect. Please go back and confirm all of your passenger information is correct.";
export const INVALID_PASSPORT_NUMBER_SUBTITLE =
  "Your passport number is not valid. Ensure all information matches your Travel ID and is up to date";
export const LAP_INFANT_TOO_OLD_SUBTITLE =
  "This passenger is too old to sit on a lap. Please go back and select a seated infant or child.";
export const LAP_INFANTS_UNSUPPORTED_SUBTITLE =
  "Lap infants are not supported on this flight. Please go back and select a seated infant to continue with the purchase.";
export const MALFORMED_KTN_SUBTITLE =
  "Your known traveler number format is incorrect. Please ensure that your information matches your correct known traveler number.";
export const MALFORMED_REDRESS_SUBTITLE =
  "Your redress number format is incorrect. Please ensure that your information matches your correct known traveler number.";
export const MISSING_AIRLINE_LOCATOR_SUBTITLE =
  "We tried to complete your booking, but encountered a technical error on our end. Please try making the booking again. If the issue keeps happening, contact customer support.";
export const MISSING_NATIONALITY_SUBTITLE =
  "It looks like we're missing your nationality. Please go back and add your country of citizenship in your passenger information.";
export const MISSING_PASSPORT_SUBTITLE =
  "Enter passport details for all passengers. Ensure all information matches your Travel ID and is up to date.";
export const NO_FLIGHT_AVAILABILITY_SUBTITLE =
  "Unfortunately, we can no longer find any availability for your selected flight(s). Please select a new flight and try again.";
export const NO_CONTACT_INFO_SUBTITLE =
  "We're missing your contact information in case we need to notify you about changes to your booking. Please fill out your phone number and your email address.";
export const TECHNICAL_ERROR_SUBTITLE =
  "We tried to complete your booking, but encountered a technical error on our end. Please try making the booking again. If the issue keeps happening, contact customer support.";
export const SEATED_INFANTS_UNSUPPORTED_SUBTITLE =
  "Seated infants are not supported on this flight. Please go back and select a lap infant to continue with the purchase.";
export const TOO_MANY_LAP_INFANTS_SUBTITLE =
  "There are too many lap infants. Please make sure you have an adult passenger for every lap infant.";
export const TOO_MANY_PASSENGERS_SUBTITLE =
  "Oops! The airline only allows you to book 6 passengers in a single reservation. Please go back and make separate reservations if you have more than 6 passengers.";

// hotels
export const LACK_OF_INVENTORY_TITLE =
  "Oh no - this hotel price is no longer available";
export const LACK_OF_INVENTORY_SUBTITLE =
  "Prices can change frequently: please try again in a few hours or try another hotel.";
export const RATE_NOT_AVAILABLE_TITLE =
  "Oh no - this hotel rate is no longer available";
export const RATE_NOT_AVAILABLE_SUBTITLE =
  "Please select another hotel rate to continue your stay.";
export const CHECK_IN_AGE_NOT_MET_TITLE = "Check-In Minimum Age Not Met";
export const CHECK_IN_AGE_NOT_MET_SUBTITLE =
  "The selected traveler does not meet the hotel’s minimum age requirements for check-in.";

// general
export const PRICE_INCREASE_TITLE = (difference: number) => {
  const productName = PRODUCT_NAME();
  return `The price per traveler for this ${productName} has increased by $${difference.toFixed()}.`;
};
export const PRICE_DECREASE_TITLE = (
  difference: number,
  requoteReason?: RequoteReason,
) => {
  const productName = PRODUCT_NAME();
  const title = `The price per traveler for this ${productName} has decreased by $${difference.toFixed()}.`;
  if (requoteReason?.RequoteReason === RequoteReasonEnum.StayDatesMismatch) {
    return title;
  }
  return `You’re in luck! ${title}`;
};

const getStayLength = (stayDates: DateRange): number =>
  dayjs(stayDates.until).diff(stayDates.from, "days");

export const PRICE_CHANGE_SUBTITLE = (requoteReason?: RequoteReason) => {
  const productName = PRODUCT_NAME();
  const explanation =
    requoteReason?.RequoteReason === RequoteReasonEnum.StayDatesMismatch
      ? `Based on the flight you selected, this ${productName} includes ${getStayLength(
          requoteReason.selectedStayDates,
        )} hotel nights instead of ${getStayLength(
          requoteReason.originalStayDates,
        )}`
      : `Sometimes ${productName} prices can change unexpectedly`;
  return `${explanation}. You can view the new ${productName} price per traveler in the checkout breakdown.`;
};
export const NO_AVAILABILITY_SUBTITLE = () => {
  const productName = PRODUCT_NAME();
  return `Unfortunately, we can no longer find any availability for this ${productName}. Please select a new ${productName} and try again.`;
};

export const SESSION_EXPIRED_TITLE = "Your session has expired";
export const SESSION_EXPIRED_SUBTITLE = () => {
  const productName = PRODUCT_NAME();
  return `Please refresh to continue or select another ${productName}.`;
};
export const REFRESH_TO_CONTINUE = "Refresh to continue";
export const GO_HOME = "Go home";
export const UNABLE_TO_CONFIRM_AVAILABILITY_TITLE =
  "Uh oh! We were unable to confirm availability for this package.";
export const UNABLE_TO_CONFIRM_AVAILABILITY_SUBTITLE =
  "Please try again or select a different package.";
